/* Responsibility: Define marketplace manifest/package contracts and registry read-write behavior.
Scope: Registry schema/cache/storage only; lobby UI orchestration is excluded. */
import localforage from 'localforage'
import type { Atlas, GameState, Room } from "@anyquest/shared-core"
import { listPublicWorlds as backendListPublicWorlds, listCreatorWorlds as backendListCreatorWorlds, saveMarketplaceWorld as backendSaveMarketplaceWorld } from "@anyquest/shared-core"
import { normalizeCommercePlanId } from '../commerce/commerce-contract'

export type MarketplaceVisibility = 'public' | 'unlisted' | 'private'
export type MarketplacePublishState = 'draft' | 'published' | 'retired'
export type MarketplacePriceModel = 'free' | 'paid'
export type MarketplaceSort = 'latest' | 'title' | 'price'

export type MarketplaceManifestDraft = Partial<{
  title: string
  intro: string
  trailerText: string
  tags: string[]
  nsfw: boolean
  previewImageUrl: string
  priceModel: MarketplacePriceModel
  priceCents: number
  currency: string
  entitlementTier: string
  visibility: MarketplaceVisibility
  publishState: MarketplacePublishState
}>

export interface MarketplaceWorldPackage {
  version: 1
  seed: string
  atlas: Atlas
  room: Room
}

export interface MarketplaceWorldManifest {
  id: string
  slug: string
  title: string
  intro: string
  trailerText: string
  creator: {
    uid: string
    displayName: string
  }
  ownership: {
    ownerUid: string
    sourceSaveId: string | null
    provenance: 'save-gallery' | 'manual'
    createdAtMs: number
    updatedAtMs: number
  }
  tags: string[]
  nsfw: boolean
  media: {
    previewImageUrl: string
    trailerText: string
  }
  pricing: {
    model: MarketplacePriceModel
    priceCents: number
    currency: string
    entitlementTier: string
  }
  visibility: MarketplaceVisibility
  publishState: MarketplacePublishState
  package: MarketplaceWorldPackage
}

export interface MarketplaceBrowseFilters {
  query?: string
  tag?: string
  priceModel?: 'all' | MarketplacePriceModel
  sort?: MarketplaceSort
}

export interface MarketplaceValidationResult {
  ok: boolean
  errors: string[]
}

const REGISTRY_CACHE_KEY = 'am2_marketplace_registry_cache_v1'
const LOCAL_REGISTRY_KEY = 'am2_marketplace_registry_local_v1'
const CACHE_TTL_MS = 90 * 1000
const CACHE_STALE_MS = 5 * 60 * 1000
const MAX_TAGS = 8

const registryCache = localforage.createInstance({
  name: 'anyquest',
  storeName: 'marketplace_registry',
})

function slugify(value: string): string {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64)
}

function shortText(value: unknown, fallback = ''): string {
  return String(value || '').replace(/\s+/g, ' ').trim() || fallback
}

function boundedTags(tags: unknown): string[] {
  const source = Array.isArray(tags) ? tags : []
  const seen = new Set<string>()
  const next = []
  for (const entry of source) {
    const tag = shortText(entry).toLowerCase().replace(/[^a-z0-9- ]/g, '').trim()
    if (!tag || seen.has(tag)) continue
    seen.add(tag)
    next.push(tag)
    if (next.length >= MAX_TAGS) break
  }
  return next
}

function deriveTags(seed: string, roomName: string): string[] {
  const source = `${seed} ${roomName}`.toLowerCase()
  const tags = []
  if (/\bforest|wood|grove|root|moss\b/.test(source)) tags.push('forest')
  if (/\bmarket|city|dock|archive|choir|registry\b/.test(source)) tags.push('urban')
  if (/\bvoid|obsidian|storm|ash|dusk|neon\b/.test(source)) tags.push('strange')
  if (/\bclock|gear|engine|machine|conduit\b/.test(source)) tags.push('clockwork')
  if (/\bsea|river|marsh|water|canal|flood\b/.test(source)) tags.push('water')
  if (!tags.length) tags.push('adventure')
  return tags
}

function summarizeIntro(seed: string, roomName: string): string {
  const intro = shortText(seed)
  if (intro.length <= 150) return intro
  const room = shortText(roomName, 'Unknown Room')
  return `${intro.slice(0, 146).trimEnd()}... Begin near ${room}.`
}

function ensurePackage(pkg: unknown): MarketplaceWorldPackage {
  const raw = pkg as Partial<MarketplaceWorldPackage> | undefined
  return {
    version: 1,
    seed: shortText(raw?.seed),
    atlas: structuredClone(raw?.atlas || { nodes: {}, edges: [] }),
    room: structuredClone(raw?.room || { name: 'Unknown Room', desc: '', npcs: [], items: [] }),
  }
}

export function validateMarketplaceWorldManifest(manifest: unknown): MarketplaceValidationResult {
  const raw = manifest as Partial<MarketplaceWorldManifest> | undefined
  const errors = []
  const title = shortText(raw?.title)
  const intro = shortText(raw?.intro)
  const creatorUid = shortText(raw?.creator?.uid)
  const creatorName = shortText(raw?.creator?.displayName)
  const pkg = ensurePackage(raw?.package)
  const pricingModel = raw?.pricing?.model === 'paid' ? 'paid' : 'free'
  const priceCents = Number.isFinite(raw?.pricing?.priceCents) ? Math.max(0, Number(raw?.pricing?.priceCents)) : 0

  if (!title) errors.push('title is required')
  if (!intro) errors.push('intro is required')
  if (!creatorUid) errors.push('creator.uid is required')
  if (!creatorName) errors.push('creator.displayName is required')
  if (!pkg.seed) errors.push('package.seed is required')
  if (!shortText(pkg.room?.name)) errors.push('package.room.name is required')
  if (pricingModel === 'paid' && priceCents <= 0) errors.push('paid listings need positive priceCents')

  return { ok: errors.length === 0, errors }
}

function cachePayload(entries: MarketplaceWorldManifest[]) {
  return { savedAtMs: Date.now(), entries }
}

async function readLocalRegistry(): Promise<MarketplaceWorldManifest[]> {
  const stored = await registryCache.getItem<MarketplaceWorldManifest[]>(LOCAL_REGISTRY_KEY)
  if (Array.isArray(stored) && stored.length) return stored
  const seeded = buildSampleRegistry()
  await registryCache.setItem(LOCAL_REGISTRY_KEY, seeded)
  return seeded
}

async function writeLocalRegistry(entries: MarketplaceWorldManifest[]): Promise<void> {
  await registryCache.setItem(LOCAL_REGISTRY_KEY, entries)
}

function buildSampleRegistry(): MarketplaceWorldManifest[] {
  const now = Date.now()
  return [
    {
      id: 'sample-choir-registry',
      slug: 'choir-registry-passage',
      title: 'Choir Registry Passage',
      intro: 'A public archive-city where memory debt, choir clerks, and contract passage turn every request into a political scene.',
      trailerText: 'Negotiate passage through a singing registry without giving away your crew.',
      creator: { uid: 'sample-system', displayName: 'AnyQuest' },
      ownership: {
        ownerUid: 'sample-system',
        sourceSaveId: null,
        provenance: 'manual',
        createdAtMs: now - 86_400_000,
        updatedAtMs: now - 86_400_000,
      },
      tags: ['urban', 'social', 'adventure'],
      nsfw: false,
      media: {
        previewImageUrl: '',
        trailerText: 'Negotiate passage through a singing registry without giving away your crew.',
      },
      pricing: {
        model: 'free',
        priceCents: 0,
        currency: 'USD',
        entitlementTier: 'free',
      },
      visibility: 'public',
      publishState: 'published',
      package: {
        version: 1,
        seed: 'A floating archive-city where memory debt is traded and every contract is recorded by choir clerks.',
        atlas: { nodes: {}, edges: [] },
        room: { name: 'Choir Ledger Dock', desc: 'Ink, rope, and registry bells surround the first approach.', npcs: [], items: [] },
      },
    },
    {
      id: 'sample-clockwork-undercity',
      slug: 'clockwork-undercity-approach',
      title: 'Clockwork Undercity',
      intro: 'A brass undercity of sealed conduits, buried observatories, and pressure chambers that reward careful exploration.',
      trailerText: 'Push through valves, conduits, and sealed rooms beneath obsidian dunes.',
      creator: { uid: 'sample-system', displayName: 'AnyQuest' },
      ownership: {
        ownerUid: 'sample-system',
        sourceSaveId: null,
        provenance: 'manual',
        createdAtMs: now - 43_200_000,
        updatedAtMs: now - 43_200_000,
      },
      tags: ['clockwork', 'exploration', 'strange'],
      nsfw: false,
      media: {
        previewImageUrl: '',
        trailerText: 'Push through valves, conduits, and sealed rooms beneath obsidian dunes.',
      },
      pricing: {
        model: 'free',
        priceCents: 0,
        currency: 'USD',
        entitlementTier: 'free',
      },
      visibility: 'public',
      publishState: 'published',
      package: {
        version: 1,
        seed: 'A clockwork undercity buried beneath obsidian dunes.',
        atlas: { nodes: {}, edges: [] },
        room: { name: 'Conduit Spine', desc: 'The first chamber hums with valves and trapped heat.', npcs: [], items: [] },
      },
    },
  ]
}

function sortEntries(entries: MarketplaceWorldManifest[], sort: MarketplaceSort) {
  const next = [...entries]
  if (sort === 'title') return next.sort((a, b) => a.title.localeCompare(b.title))
  if (sort === 'price') return next.sort((a, b) => a.pricing.priceCents - b.pricing.priceCents || b.ownership.updatedAtMs - a.ownership.updatedAtMs)
  return next.sort((a, b) => b.ownership.updatedAtMs - a.ownership.updatedAtMs)
}

export function filterMarketplaceWorlds(
  entries: MarketplaceWorldManifest[],
  filters: MarketplaceBrowseFilters = {},
): MarketplaceWorldManifest[] {
  const queryText = shortText(filters.query).toLowerCase()
  const tag = shortText(filters.tag).toLowerCase()
  const priceModel = filters.priceModel || 'all'
  const sort = filters.sort || 'latest'

  const filtered = entries.filter(entry => {
    if (entry.publishState !== 'published' || entry.visibility !== 'public') return false
    if (priceModel !== 'all' && entry.pricing.model !== priceModel) return false
    if (tag && !entry.tags.includes(tag)) return false
    if (!queryText) return true
    const haystack = [
      entry.title,
      entry.intro,
      entry.trailerText,
      entry.creator.displayName,
      ...entry.tags,
    ].join(' ').toLowerCase()
    return haystack.includes(queryText)
  })

  return sortEntries(filtered, sort)
}

async function fetchFreshRegistry(): Promise<MarketplaceWorldManifest[]> {
  let entries: MarketplaceWorldManifest[] = []
  try {
    entries = (await backendListPublicWorlds())
      .map(row => row as MarketplaceWorldManifest)
      .filter(entry => validateMarketplaceWorldManifest(entry).ok)
    
    if (!entries.length) entries = await readLocalRegistry()
  } catch (error) {
    console.warn('[marketplace] Remote registry fetch failed, falling back to local.', error)
    entries = await readLocalRegistry()
  }
  await registryCache.setItem(REGISTRY_CACHE_KEY, cachePayload(entries))
  return entries
}

async function fetchCreatorRegistry(creatorUid: string): Promise<MarketplaceWorldManifest[]> {
  const uid = shortText(creatorUid)
  if (!uid) return []
  try {
    const entries = (await backendListCreatorWorlds(uid))
      .map(row => row as MarketplaceWorldManifest)
      .filter(entry => validateMarketplaceWorldManifest(entry).ok)
    if (entries.length) return sortEntries(entries, 'latest')
  } catch (error) {
    console.warn('[marketplace] Creator registry fetch failed, falling back to local.', error)
  }
  const local = await readLocalRegistry()
  return sortEntries(
    local.filter(entry => entry.creator?.uid === uid || entry.ownership?.ownerUid === uid),
    'latest',
  )
}

export async function listMarketplaceWorlds(filters: MarketplaceBrowseFilters = {}): Promise<MarketplaceWorldManifest[]> {
  const cached = await registryCache.getItem<{ savedAtMs: number; entries: MarketplaceWorldManifest[] }>(REGISTRY_CACHE_KEY)
  const age = cached?.savedAtMs ? Date.now() - cached.savedAtMs : Infinity
  const hasCached = Array.isArray(cached?.entries) && cached!.entries.length > 0

  // Fresh cache — serve directly
  if (hasCached && age < CACHE_TTL_MS) {
    return filterMarketplaceWorlds(cached!.entries, filters)
  }

  // Stale cache — serve stale, refresh in background
  if (hasCached && age < CACHE_STALE_MS) {
    fetchFreshRegistry().catch(() => {})
    return filterMarketplaceWorlds(cached!.entries, filters)
  }

  // No cache or expired — fetch fresh
  const entries = await fetchFreshRegistry()
  return filterMarketplaceWorlds(entries, filters)
}

export async function listMarketplaceWorldRegistry(): Promise<MarketplaceWorldManifest[]> {
  const cached = await registryCache.getItem<{ savedAtMs: number; entries: MarketplaceWorldManifest[] }>(REGISTRY_CACHE_KEY)
  const age = cached?.savedAtMs ? Date.now() - cached.savedAtMs : Infinity
  const hasCached = Array.isArray(cached?.entries) && cached!.entries.length > 0

  if (hasCached && age < CACHE_TTL_MS) return sortEntries(cached!.entries, 'latest')
  if (hasCached && age < CACHE_STALE_MS) {
    fetchFreshRegistry().catch(() => {})
    return sortEntries(cached!.entries, 'latest')
  }

  return sortEntries(await fetchFreshRegistry(), 'latest')
}

export async function listCreatorWorldRegistry(creatorUid: string): Promise<MarketplaceWorldManifest[]> {
  return fetchCreatorRegistry(creatorUid)
}

export async function clearMarketplaceRegistryCache(): Promise<void> {
  await registryCache.removeItem(REGISTRY_CACHE_KEY)
}

export function marketplaceTags(entries: MarketplaceWorldManifest[]): string[] {
  return [...new Set(entries.flatMap(entry => entry.tags))].sort((a, b) => a.localeCompare(b))
}

export function buildMarketplaceManifestFromState(
  state: GameState,
  options: {
    creatorUid: string
    creatorName: string
    sourceSaveId?: string | null
    draft?: MarketplaceManifestDraft
  },
): MarketplaceWorldManifest {
  const now = Date.now()
  const roomName = shortText(state.currentRoom?.name, 'Unknown Room')
  const seed = shortText(state.world?.seed)
  const title = shortText(options.draft?.title, roomName)
  const intro = shortText(options.draft?.intro, summarizeIntro(seed, roomName))
  const trailerText = shortText(options.draft?.trailerText, `Begin in ${roomName} and carry the world forward from there.`)
  const tags = boundedTags(options.draft?.tags)
  const pricingModel = options.draft?.priceModel === 'paid' ? 'paid' : 'free'
  const priceCents = pricingModel === 'paid'
    ? Math.max(0, Number(options.draft?.priceCents || 0))
    : 0

  return {
    id: `${slugify(title)}-${now.toString(36)}`,
    slug: slugify(title) || `world-${now.toString(36)}`,
    title,
    intro,
    trailerText,
    creator: {
      uid: shortText(options.creatorUid),
      displayName: shortText(options.creatorName, 'Unknown Creator'),
    },
    ownership: {
      ownerUid: shortText(options.creatorUid),
      sourceSaveId: shortText(options.sourceSaveId) || null,
      provenance: options.sourceSaveId ? 'save-gallery' : 'manual',
      createdAtMs: now,
      updatedAtMs: now,
    },
    tags: tags.length ? tags : deriveTags(seed, roomName),
    nsfw: options.draft?.nsfw === true,
    media: {
      previewImageUrl: shortText(options.draft?.previewImageUrl),
      trailerText,
    },
    pricing: {
      model: pricingModel,
      priceCents,
      currency: shortText(options.draft?.currency, 'USD'),
      entitlementTier: normalizeCommercePlanId(options.draft?.entitlementTier ?? (pricingModel === 'paid' ? 'pro' : 'free')),
    },
    visibility: options.draft?.visibility === 'unlisted'
      ? 'unlisted'
      : options.draft?.visibility === 'private'
        ? 'private'
        : 'public',
    publishState: options.draft?.publishState === 'draft'
      ? 'draft'
      : options.draft?.publishState === 'retired'
        ? 'retired'
        : 'published',
    package: {
      version: 1,
      seed,
      atlas: structuredClone(state.atlas),
      room: structuredClone(state.currentRoom),
    },
  }
}

export async function publishMarketplaceWorld(
  state: GameState,
  options: {
    creatorUid: string
    creatorName: string
    sourceSaveId?: string | null
    draft?: MarketplaceManifestDraft
  },
): Promise<MarketplaceWorldManifest> {
  const manifest = buildMarketplaceManifestFromState(state, options)
  const validation = validateMarketplaceWorldManifest(manifest)
  if (!validation.ok) {
    throw new Error(`Invalid marketplace manifest: ${validation.errors.join('; ')}`)
  }

  try {
    await backendSaveMarketplaceWorld(manifest)
  } catch (error) {
    console.warn('[marketplace] Remote publish failed, falling back to local registry.', error)
    const existing = await readLocalRegistry()
    await writeLocalRegistry(sortEntries([manifest, ...existing.filter(entry => entry.id !== manifest.id)], 'latest'))
  }

  await clearMarketplaceRegistryCache()
  return manifest
}

export async function upsertMarketplaceWorldManifest(manifest: MarketplaceWorldManifest): Promise<MarketplaceWorldManifest> {
  const validation = validateMarketplaceWorldManifest(manifest)
  if (!validation.ok) {
    throw new Error(`Invalid marketplace manifest: ${validation.errors.join('; ')}`)
  }

  const nextManifest: MarketplaceWorldManifest = {
    ...manifest,
    ownership: {
      ...manifest.ownership,
      updatedAtMs: Date.now(),
    },
  }

  try {
    await backendSaveMarketplaceWorld(nextManifest)
  } catch (error) {
    console.warn('[marketplace] Remote upsert failed, falling back to local registry.', error)
    const existing = await readLocalRegistry()
    await writeLocalRegistry(sortEntries([nextManifest, ...existing.filter(entry => entry.id !== nextManifest.id)], 'latest'))
  }

  await clearMarketplaceRegistryCache()
  return nextManifest
}

export function marketplaceManifestToImportedWorldData(manifest: MarketplaceWorldManifest): {
  seed: string
  atlas: Atlas
  room: Room
} {
  return {
    seed: manifest.package.seed,
    atlas: structuredClone(manifest.package.atlas),
    room: structuredClone(manifest.package.room),
  }
}
